// 主页：顶部 session/tool 指示 + 中部聊天流 + 底部双 PC 胶囊 + 输入条 + 左 Drawer。
//
// Day-4 版本极简化：
//   - 双胶囊只显示在线/离线（轮询 /api/agents 5s 一次）
//   - 输入条还不能真发命令（Day-5 才接 send_command 路由）
//   - 流式输出区会显示 ws.log（调试用），等 Day-5 真接 claude 后再渲染聊天气泡
//
// UI 设计参考 R5 #4 决议：底部双胶囊（emoji + 颜色），左 Drawer 切 session。

import { useEffect, useState } from "preact/hooks";
import { effect } from "@preact/signals";
import {
  agents,
  agentsError,
  agentsLastFetch,
  agentsLoading,
  startAgentsPolling,
  stopAgentsPolling
} from "../state/agents";
import {
  chat,
  handleEnvelope,
  pushChat,
  pushLog,
  selectedAgentId,
  selectedCwd,
  selectedResumeId,
  continueRecent,
  wsLog,
  wsState,
  wsStateDetail
} from "../state/session";
import { WSClient } from "../transport/ws";
import { CliRPC } from "../transport/cli";
import { tokenStore } from "../storage/tokens";
import {
  getSubscriptionState,
  subscribePush,
  triggerPushTest,
  unsubscribePush,
} from "../transport/push";
import { ProjectPicker } from "../components/ProjectPicker";

let wsClient: WSClient | null = null;
let cliRpc: CliRPC | null = null;

function ensureWS() {
  if (wsClient) return wsClient;
  wsClient = new WSClient({
    onState: (s, d) => {
      wsState.value = s;
      wsStateDetail.value = d ?? "";
    },
    onLog: (line) => pushLog(line),
    onMessage: (env) => {
      // Day-8: 先让 CliRPC 拦截 ack_id 匹配的回复，没匹配再交给 session handler
      if (cliRpc?.handleEnvelope(env)) return;
      handleEnvelope(env);
    }
  });
  cliRpc = new CliRPC(wsClient);
  wsClient.start();
  return wsClient;
}

export function HomePage({ onLogout }: { onLogout: () => void }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [input, setInput] = useState("");
  const [identityAlias, setIdentityAlias] = useState("📱");
  const [pushPerm, setPushPerm] = useState<NotificationPermission>("default");
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState<string | null>(null);

  async function refreshPushState() {
    const s = await getSubscriptionState();
    setPushPerm(s.permission);
    setPushSubscribed(s.subscribed);
  }

  async function onTogglePush() {
    setPushBusy(true);
    setPushMsg(null);
    try {
      if (pushSubscribed) {
        await unsubscribePush();
        setPushMsg("已取消订阅");
      } else {
        const r = await subscribePush();
        setPushMsg(r.ok ? "已订阅 ✓" : `失败：${r.reason}`);
      }
    } catch (e) {
      setPushMsg(`错误：${(e as Error).message}`);
    } finally {
      setPushBusy(false);
      void refreshPushState();
    }
  }

  async function onPushTest() {
    setPushBusy(true);
    setPushMsg(null);
    try {
      await triggerPushTest();
      setPushMsg("已触发测试推送（Bark + Web Push）");
    } catch (e) {
      setPushMsg(`错误：${(e as Error).message}`);
    } finally {
      setPushBusy(false);
    }
  }

  useEffect(() => {
    void (async () => {
      const id = await tokenStore.getIdentity();
      if (id) setIdentityAlias(id.alias);
    })();
    void refreshPushState();
    startAgentsPolling(5000);
    ensureWS();
    return () => {
      stopAgentsPolling();
      wsClient?.stop();
      wsClient = null;
    };
  }, []);

  // 默认选第一台在线 agent
  useEffect(() => {
    return effect(() => {
      if (selectedAgentId.value) return;
      const first = agents.value.find((a) => a.online) ?? agents.value[0];
      if (first) selectedAgentId.value = first.id;
    });
  }, []);

  function onSubmit(e: Event) {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    if (!selectedAgentId.value) {
      pushChat({ role: "system", text: "未选中 PC", ts: Date.now() });
      return;
    }
    if (wsState.value !== "open") {
      pushChat({ role: "system", text: `WS 未连接（${wsState.value}）`, ts: Date.now() });
      return;
    }
    const sessionId = `session-${selectedAgentId.value}-${Date.now()}`;
    pushChat({ role: "user", text, ts: Date.now() });
    wsClient?.send("send_command", {
      target_agent_id: selectedAgentId.value,
      session_id: sessionId,
      content: text,
      // Day-8 三个新字段（空字符串 / false 时 agent 用默认行为）
      cwd: selectedCwd.value || "",
      resume_session_id: selectedResumeId.value || "",
      continue_recent: continueRecent.value,
    });
    setInput("");
  }

  // 当前续接策略的简短描述
  const resumeLabel = selectedResumeId.value
    ? `续接 ${selectedResumeId.value.slice(0, 8)}`
    : continueRecent.value
    ? "续接最近一次"
    : "新对话";

  function cwdShort(): string {
    const c = selectedCwd.value;
    if (!c) return "未选择目录";
    // 截短：只显示最后两段
    const parts = c.split(/[\\\/]+/).filter(Boolean);
    return parts.length > 2 ? "…" + parts.slice(-2).join("/") : c;
  }

  return (
    <div class="page page-home">
      {/* 顶栏 */}
      <header class="topbar">
        <button
          class="icon-btn"
          aria-label="侧栏"
          onClick={() => setDrawerOpen(true)}
        >
          ☰
        </button>
        <div class="topbar__title">
          <span class="alias">{identityAlias}</span>
          <span class="ws-state" data-state={wsState.value}>
            {wsState.value}
            {wsStateDetail.value ? ` · ${wsStateDetail.value}` : ""}
          </span>
        </div>
        <button class="icon-btn" aria-label="退出" onClick={onLogout}>
          ⏻
        </button>
      </header>

      {/* 聊天流 */}
      <main class="chat">
        {chat.value.length === 0 && (
          <div class="empty">
            <p>选一台 PC，输入命令开始</p>
            <p class="muted">
              Day-4 阶段输入只发到 WS，Agent Day-5 才接 claude CLI；
              当前可用来验证连接。
            </p>
          </div>
        )}
        {chat.value.map((m, i) => (
          <div class={`msg msg-${m.role}`} key={i}>
            <div class="msg__bubble">{m.text}</div>
          </div>
        ))}
        {/* WS log 调试条（默认折叠） */}
        <details class="ws-log">
          <summary>WS log ({wsLog.value.length})</summary>
          <pre>{wsLog.value.join("\n")}</pre>
        </details>
      </main>

      {/* 底部双胶囊 + 输入条 */}
      <footer class="bottom">
        <div class="capsules">
          {agentsLoading.value && agents.value.length === 0 && (
            <div class="capsules__loading">加载…</div>
          )}
          {agentsError.value && (
            <div class="capsules__error" title={agentsError.value}>
              ⚠ 拉取失败
            </div>
          )}
          {agents.value.map((a) => {
            const sel = selectedAgentId.value === a.id;
            return (
              <button
                key={a.id}
                class={`capsule${sel ? " capsule--selected" : ""}${
                  a.online ? " capsule--online" : " capsule--offline"
                }`}
                onClick={() => (selectedAgentId.value = a.id)}
              >
                <span class="capsule__alias">{a.alias}</span>
                <span class="capsule__dot" />
                <span class="capsule__id">{a.id}</span>
              </button>
            );
          })}
        </div>

        {/* Day-8 项目目录条 */}
        <div class="project-bar">
          <button
            type="button"
            class="project-bar__btn"
            onClick={() => setPickerOpen(true)}
            disabled={!selectedAgentId.value || wsState.value !== "open"}
            title="选项目目录 + 续接策略"
          >
            <span class="project-bar__icon">📁</span>
            <span class="project-bar__cwd">{cwdShort()}</span>
            <span class={`project-bar__resume ${(selectedResumeId.value || continueRecent.value) ? "project-bar__resume--active" : ""}`}>
              {resumeLabel}
            </span>
            <span class="project-bar__arrow">▾</span>
          </button>
        </div>

        <form class="composer" onSubmit={onSubmit}>
          <input
            class="composer__input"
            value={input}
            onInput={(e) => setInput((e.target as HTMLInputElement).value)}
            placeholder={
              selectedAgentId.value
                ? `→ ${selectedAgentId.value}`
                : "选一台 PC"
            }
            disabled={!selectedAgentId.value || wsState.value !== "open"}
          />
          <button
            class="composer__send"
            type="submit"
            disabled={
              !selectedAgentId.value ||
              wsState.value !== "open" ||
              !input.trim()
            }
          >
            ↑
          </button>
        </form>

        <div class="status-line muted">
          {agentsLastFetch.value > 0 &&
            `last update ${new Date(
              agentsLastFetch.value * 1000
            ).toLocaleTimeString()}`}
        </div>
      </footer>

      {/* Day-8 项目选择器 */}
      {pickerOpen && (
        <ProjectPicker rpc={cliRpc} onClose={() => setPickerOpen(false)} />
      )}

      {/* 左 Drawer */}
      {drawerOpen && (
        <div class="drawer-mask" onClick={() => setDrawerOpen(false)}>
          <aside class="drawer" onClick={(e) => e.stopPropagation()}>
            <h2>设置</h2>

            <section class="drawer-section">
              <h3>通知推送</h3>
              <p class="muted small">
                授权后，PC 离线 / 任务完成时会推到 iPhone 锁屏。
                Bark 走 https://api.day.app；Web Push 走 VAPID。
              </p>
              <div class="push-state">
                <span>权限：<b>{pushPerm}</b></span>
                <span>订阅：<b>{pushSubscribed ? "✓" : "—"}</b></span>
              </div>
              <div class="row">
                <button
                  class="btn btn-primary"
                  disabled={pushBusy}
                  onClick={onTogglePush}
                >
                  {pushSubscribed ? "取消订阅" : "授权 + 订阅"}
                </button>
                <button class="btn" disabled={pushBusy} onClick={onPushTest}>
                  发测试
                </button>
              </div>
              {pushMsg && <div class="muted small">{pushMsg}</div>}
            </section>

            <section class="drawer-section">
              <h3>会话历史</h3>
              <p class="muted small">Day-7+ 实施（持久化到 GW SQLite）</p>
            </section>

            <button onClick={onLogout} class="btn btn-danger">退出登录</button>
          </aside>
        </div>
      )}
    </div>
  );
}
